import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const TrackOticAuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function TrackOticAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [scopeSummary, setScopeSummary] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('trackotic_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/v1/auth/me`);
      const { user: userData, tenant: tenantData, permissions: perms, roles: userRoles, scope_summary } = response.data;
      setUser(userData);
      setTenant(tenantData);
      setPermissions(perms || []);
      setRoles(userRoles || []);
      setScopeSummary(scope_summary);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE}/api/v1/auth/login`, { email, password });
    const { access_token, user: userData, tenant: tenantData } = response.data;
    localStorage.setItem('trackotic_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setTenant(tenantData);
    // Fetch full user info including permissions
    await fetchUserInfo();
    return { user: userData, tenant: tenantData };
  };

  const logout = () => {
    localStorage.removeItem('trackotic_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setTenant(null);
    setPermissions([]);
    setRoles([]);
    setScopeSummary(null);
  };

  const hasPermission = (permissionCode) => {
    return permissions.includes(permissionCode) || permissions.includes('admin');
  };

  const hasAnyPermission = (permissionCodes) => {
    return permissionCodes.some(code => hasPermission(code));
  };

  // Check if user is SentinelX (platform owner)
  const isSentinelX = () => {
    return tenant?.code === 'SENTINELX';
  };

  // Check if user has tenant-wide access (no location scope)
  const hasTenantWideAccess = () => {
    return scopeSummary?.has_tenant_wide_access || false;
  };

  return (
    <TrackOticAuthContext.Provider value={{ 
      user, 
      tenant, 
      token, 
      loading, 
      permissions,
      roles,
      scopeSummary,
      login, 
      logout,
      hasPermission,
      hasAnyPermission,
      isSentinelX,
      hasTenantWideAccess,
      refreshUser: fetchUserInfo
    }}>
      {children}
    </TrackOticAuthContext.Provider>
  );
}

export const useTrackOticAuth = () => {
  const context = useContext(TrackOticAuthContext);
  if (!context) {
    throw new Error('useTrackOticAuth must be used within an TrackOticAuthProvider');
  }
  return context;
};
